import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Users,
  MapPin,
  ClipboardList,
  ScanFace,
  FileBarChart,
  Route as RouteIcon,
  CreditCard,
  Bell,
  BookOpen,
  LogOut,
  ShieldCheck,
  Menu,
  X,
  KeyRound,
  Building2,
  Coffee,
  Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import SiteFooter from "@/components/SiteFooter";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard", always: true },
  { to: "/users", label: "User Management", icon: ShieldCheck, testid: "sidebar-nav-users", superOnly: true },
  { to: "/employees", label: "Employees", icon: Users, testid: "sidebar-nav-employees", perm: "employees" },
  { to: "/businesses", label: "Businesses", icon: Building2, testid: "sidebar-nav-businesses", perm: "businesses" },
  { to: "/sites", label: "Sites", icon: MapPin, testid: "sidebar-nav-sites", perm: "sites" },
  { to: "/attendance", label: "Attendance Logs", icon: ClipboardList, testid: "sidebar-nav-attendance", perm: "attendance" },
  { to: "/desk-sessions", label: "Desk Sessions", icon: Coffee, testid: "sidebar-nav-desk-sessions", perm: "attendance" },
  { to: "/reviews", label: "AI Review Queue", icon: ScanFace, testid: "sidebar-nav-ai-queue", perm: "reviews" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "sidebar-nav-reports", perm: "reports" },
  { to: "/journey", label: "Employee Journey", icon: RouteIcon, testid: "sidebar-nav-journey", perm: "reports", feature: "journey_history" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "sidebar-nav-billing", superOnly: true },
  { to: "/notifications", label: "Notification Settings", icon: Bell, testid: "sidebar-nav-notifications", superOnly: true },
  { to: "/push-broadcast", label: "Push Broadcast", icon: Megaphone, testid: "sidebar-nav-push-broadcast", superOnly: true },
  { to: "/help", label: "Help & Manual", icon: BookOpen, testid: "sidebar-nav-help", superOnly: true },
];

function navVisible(item, me) {
  if (item.feature && me?.role !== "super_admin" && me?.features && me.features[item.feature] === false) return false;
  if (me?.role === "super_admin") return true;
  if (item.always) return true;
  if (item.superOnly) return false;
  if (item.perm) return (me?.permissions || []).includes(item.perm);
  return false;
}

const TITLES = {
  "/dashboard": "Dashboard Overview",
  "/users": "User Management",
  "/employees": "Employees",
  "/businesses": "Businesses",
  "/sites": "Sites & Geofencing",
  "/attendance": "Attendance Logs",
  "/desk-sessions": "Desk Sessions",
  "/reviews": "AI Review Queue",
  "/billing": "Billing & Subscription",
  "/notifications": "Notification Settings",
  "/push-broadcast": "Push Broadcast",
  "/reports": "Reports",
  "/journey": "Employee Journey",
  "/help": "Help & User Manual",
};

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", staff: "Staff" };

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="hidden md:flex flex-col items-end leading-tight" data-testid="header-clock">
      <span className="text-sm font-semibold text-slate-800 font-mono">
        {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </span>
      <span className="text-xs text-slate-400">
        {now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
      </span>
    </div>
  );
}

export default function Layout() {
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const title = TITLES[location.pathname] || "Admin Portal";
  const initials = (me?.name || "A")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const doSignOut = () => {
    signOut();
    navigate("/login", { replace: true });
  };

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-white/5">
        <img src="/logo.webp" alt="Smart-In" width="40" height="40" loading="eager" decoding="async"
          className="h-10 w-10 rounded-xl object-cover shadow-lg shadow-indigo-900/40 bg-white" />
        <div className="leading-tight">
          <p className="text-white font-head font-extrabold text-[15px] tracking-tight">Smart-In</p>
          <p className="text-[11px] text-slate-400">Admin Portal</p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {NAV.filter((item) => navVisible(item, me)).map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/30"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="grid place-items-center h-9 w-9 rounded-full bg-slate-700 text-white text-xs font-bold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{me?.name}</p>
            <p className="text-[11px] text-slate-400">{me?.title || ROLE_LABEL[me?.role] || me?.role}</p>
          </div>
        </div>
        <button
          data-testid="change-password-btn"
          onClick={() => {
            setOpen(false);
            setShowPwd(true);
          }}
          className="mt-2 w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
        >
          <KeyRound className="h-[18px] w-[18px]" />
          Change Password
        </button>
        <button
          data-testid="header-signout-btn"
          onClick={doSignOut}
          className="mt-1 w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 hover:text-white hover:bg-red-500/10 hover:text-red-300 transition-colors"
        >
          <LogOut className="h-[18px] w-[18px]" />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 flex-shrink-0 bg-slate-900 sticky top-0 h-screen">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-slate-900 h-full">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-slate-600"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-btn"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <img src="/logo.webp" alt="Smart-In" width="32" height="32" loading="eager" decoding="async"
              className="lg:hidden h-8 w-8 rounded-lg object-cover bg-white ring-1 ring-slate-200" />
            <h1 className="font-head text-lg sm:text-xl font-bold text-slate-900 tracking-tight" data-testid="page-title">
              {title}
            </h1>
            {me?.business_name && (
              <>
                <span className="hidden sm:block h-5 w-px bg-slate-200" />
                <span className="hidden sm:inline-flex items-center gap-1.5 font-head text-base sm:text-lg font-bold text-indigo-600 tracking-tight" data-testid="header-business-name">
                  <Building2 className="h-4 w-4" />
                  {me.business_name}
                </span>
                {me.business_number && (
                  <span className="hidden sm:inline-flex items-center h-7 px-2.5 rounded-full bg-[#FFF4CC] border border-[#FFC400]/60 font-mono text-xs font-bold text-[#1A1500]" data-testid="header-business-number">
                    Customer No. {me.business_number}
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Clock />
            <div className="grid place-items-center h-9 w-9 rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold border border-indigo-100">
              {initials}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>

        <SiteFooter />
      </div>

      <ChangePasswordModal open={showPwd} onClose={() => setShowPwd(false)} />
    </div>
  );
}
